import { call, takeEvery, put } from 'redux-saga/effects';
import { CloudActions } from '@portal/portal-core';

import { SuperAdminActions } from 'actions';
import { sendAction, type WrapResult } from '../api';
import { transformUser } from '../shared';
import { unPhantomUsersSuccess } from '../actions';
import { SUPER_ADMIN_REQUEST_UN_PHANTOM_USERS } from '../types';

// Minimal shape of a super-admin user acted on by this saga.
interface SuperUser {
  id: string;
  [key: string]: unknown;
}

// Config threaded through the saga: super-client request config plus the local
// user database used to persist the transformed server response.
interface UnPhantomConfig {
  getAuthToken: () => string;
  baseApiUrl: string;
  timeout?: number;
  db: {
    users: {
      update: (id: unknown, user: unknown) => unknown;
    };
  };
}

// Action dispatched to trigger the un-phantom-users saga.
interface UnPhantomUsersAction {
  type: string;
  users: SuperUser[];
}

function* unPhantomUserSaga(
  config: UnPhantomConfig,
  user: SuperUser,
): Generator<unknown, WrapResult, unknown> {
  try {
    const action = CloudActions.adminSetUserPhantom(user.id, false);
    const res = (yield call(sendAction, config, action)) as WrapResult;
    if (res.succeeded) {
      yield config.db.users.update(
        (res.data as { id: string }).id,
        transformUser(res.data as Parameters<typeof transformUser>[0]),
      );
    }
    return { ...res, data: transformUser(res.data as Parameters<typeof transformUser>[0]) };
  } catch (error) {
    return { succeeded: false, data: user };
  }
}

export function* unPhantomUsersSaga(
  config: UnPhantomConfig,
  action: UnPhantomUsersAction,
): Generator<unknown, void, unknown> {
  try {
    const { users } = action;
    const responses: WrapResult[] = [];
    for (let i = 0; i < users.length; i++) {
      const res = (yield unPhantomUserSaga(config, users[i])) as WrapResult;
      responses.push(res);
    }
    yield put(unPhantomUsersSuccess(responses));
    yield put(SuperAdminActions.deselectAllUserRows());
  } catch (error) {
    console.error('unPhantomUsers failed -- ', error);
  }
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchUnPhantomUsers(
  config: UnPhantomConfig,
): Generator<unknown, void, unknown> {
  yield takeEvery(SUPER_ADMIN_REQUEST_UN_PHANTOM_USERS, unPhantomUsersSaga, config);
}
